// utils/api.ts
import axios from 'axios';
import { Category, Product, PaymentPayload } from './types';  // Make sure the import is from 'types.ts'

// Accessing the API URL from environment variables
const API_URL = process.env.REACT_APP_API_URL;

// Create an axios instance with the base URL
export const api = axios.create({
    baseURL: API_URL,
});

// Add a response interceptor
api.interceptors.response.use(
    (response) => response, // Pass through successful responses
    (error) => {
        // Check for a 403 error
        if (error.response?.status === 403) {
            // Clear local storage and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = "/login"; // Force navigation
        } else if (error.response?.data?.message === "Token expired") {
            // Handle token expiration separately if needed
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = "/login"; // Force navigation
        }

        console.error('API Error:', error);
        return Promise.reject(error);
    }
);


// Function to log in a user
export const login = async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response;
};

// utils/api.ts

// Function to fetch vendor settings
export const fetchVendorSettings = async (vendorId: number) => {
    const token = localStorage.getItem('token'); // Retrieve the stored token
    const response = await api.get(`/Setting/List/${vendorId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    // console.log('fetchVendorSettings', vendorId, response.data);

    return response.data;
};

// Function to fetch vendor settings
export const fetchVendorTaxSettings = async (vendorId: number) => {
    const token = localStorage.getItem('token'); // Retrieve the stored token
    const response = await api.get(`VendorTaxes/WithTaxTypes/${vendorId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    // console.log('fetchVendorTaxSettings: ',vendorId, response.data);

    return response.data[0];
};

// Fetch categories using the axios instance
export const fetchCategories = async (): Promise<Category[]> => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const vendorId = userData.vendor_id; // Get vendor ID from user data

    // Use template literals to embed vendorId in the URL
    const response = await api.get(`/Categorie/List/${vendorId}`, {
        headers: {
            Authorization: `Bearer ${token}`, // Include the token in the headers
        },
    });

    // console.log(vendorId, response.data);

    return response.data; // Ensure this matches the structure of your Category type
};

export const fetchProductsByCategory = async (categoryId: number) => {
    const token = localStorage.getItem('token'); // Get the stored token

    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const vendorId = userData.vendor_id; // Get vendor ID from user data
    const response = await api.get(`/Product/Category/${vendorId}/${categoryId}`, {
        headers: {
            Authorization: `Bearer ${ token }`, // Send the token in the Authorization header
        },
    });

    // console.log('fetchProductsByCategory:', response.data.products);

    return response.data.products;
};

// src/utils/api.ts

// Fetch products by vendorId and categoryId
// src/utils/api.ts

export const fetchProducts = async (vendorId: number, categoryId: number): Promise<Product[]> => {
    const token = localStorage.getItem('token'); // Get the stored token
    const response = await api.get(`/Product/Category/${vendorId}/${categoryId}`, {
        headers: {
            Authorization: `Bearer ${token}`, // Send the token in the Authorization header
        },
    });

    // console.log('Fetched Products:', response.data.products); // Log the fetched data
    return response.data.products; // Ensure this matches the structure of your Product type
};

// Function to fetch all products across all categories
export const fetchAllProducts = async (vendorId: number): Promise<Product[]> => {
    try {
        // Fetch all categories first
        const categories = await fetchCategories();

        // Initialize an empty array to store all products
        let allProducts: Product[] = [];

        // Fetch products for each category using Promise.all
        const productPromises = categories.map(async (category) => {
            const products = await fetchProducts(vendorId, category.id);
            return products;
        });

        // Resolve all promises and flatten the results into a single array
        const productsByCategory = await Promise.all(productPromises);
        allProducts = productsByCategory.flat(); // Flatten the array of arrays into a single array

        return allProducts;
    } catch (err) {
        console.error('Error fetching all products:', err);
        throw err; // Handle the error as needed in your app
    }
};

// src/utils/api.ts

export const fetchProductSpecifications = async (vendorId: number, productId: number): Promise<any[]> => {
    const token = localStorage.getItem('token'); // Get the stored token
    const response = await api.get(`/Product/Specifications/${vendorId}/${productId}`, {
        headers: {
            Authorization: `Bearer ${token}`, // Send the token in the Authorization header
        },
    });

    // console.log('fetchProductSpecifications Products:', response.data); // Log the fetched data

    return response.data.specifications; // Ensure this matches the structure of your specification type
};

// utils/api.ts

export const fetchOrderDetails = async (vendorId: number, orderId: number) => {
    const token = localStorage.getItem('token');
    const response = await api.get(`/Pos/Order/${vendorId}/${orderId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    // console.log('fetchOrderDvendorId, numericOrderIdetails:', response);
    return response.data;
};

// utils/api.ts

export const fetchOrderHistory = async (vendorId: number, status: string, page: number = 1, limit: number = 10) => {

    const token = localStorage.getItem('token');
    const response = await api.get(`/Pos/History/${vendorId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        params: {
            status,
            page,
            limit,
        },
    });

    // console.log('fetchOrderHistory:', response);

    return response.data; // Adjust based on the API response structure
};

// utils/api.ts
// Function to fetch table list by vendorId
export const fetchTableList = async () => {
    const token = localStorage.getItem('token');

    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const vendorId = userData.vendor_id; // Get vendor ID from user data

    const response = await api.get(`${API_URL}Table/List/${vendorId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    // console.log('fetchTableList:', response);

    return response.data;
};

//at /utils/api.ts





// src/utils/api.ts

export const cancelOrder = async (orderId: number) => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const vendorId = userData.vendor_id; // Get vendor ID from user data

    const response = await axios.put(
        `https://api.freegopos.com/Pos/Order/Status/${vendorId}`,
        {
            orderId: orderId,
            status: 'cancel'
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );

    return response.data; // Assuming the API returns a success message
};

// Fetch payment methods for a vendor
export const fetchPaymentMethods = async (vendorId: number) => {
    const token = localStorage.getItem('token');
    const response = await api.get(`/PaymentMethod/List/${vendorId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    // console.log('fetchPaymentMethods:', response);

    return response.data;
};



export const completePayment = async (vendorId: number, payload: PaymentPayload) => {
    const token = localStorage.getItem('token');
    const response = await api.post(`/Pos/Payments/${vendorId}`, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

// src/utils/api.ts


export async function retrieveDailyReport(vendorId: string, token: string, startDatetime: string, endDatetime: string) {
    try {
        const response = await axios.post(
            `${API_URL}Report/Daily/${vendorId}`,
            { vendorId, startDatetime, endDatetime },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error retrieving daily report:", error);
        throw error;
    }
}

export async function getExportData(vendorId: string, token: string, startDatetime: string, endDatetime: string) {
    try {
        const response = await axios.post(
            `${API_URL}Report/OrderDetails/${vendorId}`,
            { vendorId, startDatetime, endDatetime },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error retrieving export data:", error);
        throw error;
    }
}

// import axios from 'axios';


// src/utils/api.ts


export const createOrder = async (vendorId: string, payload: any, token: string) => {
    // const token = localStorage.getItem('token');
    // const userData = JSON.parse(localStorage.getItem('user') || '{}');
    // const vendorId = userData.vendor_id; // Get vendor ID from user data

    const url = `${API_URL}Pos/Order/${vendorId}`;
    const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };

    return axios.post(url, payload, { headers });
};

// export const createOrder = async (payload: string) => {
//     const token = localStorage.getItem('token');
//     const userData = JSON.parse(localStorage.getItem('user') || '{}');
//     const vendorId = userData.vendor_id; // Get vendor ID from user data

//     const response = await axios.post(`${API_URL}Pos/Order/${vendorId}`, payload, {
//         headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json',
//         },
//     });

//     console.log('CreateOrder:', response);

//     return response;
// };

export const updateOrder = async (vendorId: number, orderId: number, payload: any, token: string) => {
    const url = `${API_URL}Pos/Reorder/${vendorId}/${orderId}`;
    const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };

    return axios.post(url, payload, { headers });
};


export const postReOrder = async (
    vendorId: string,
    orderId: string,
    payload: any,
    token: string | null
) => {
    const url = `${API_URL}Pos/ReOrder/${vendorId}/${orderId}`;
    const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };

    return axios.post(url, payload, { headers });
};

export const updateTableStatus = async (
    vendorId: number,
    tableId: number,
    status: string,
    orderId:  number | null,
    customerId: number | null,
    // token: string
) => {

    // console.log("updateTableStatus called with:", { vendorId, tableId, status, orderId, customerId });
    try {
        const token = localStorage.getItem('token');
        const url = `${API_URL}Table/Update/${vendorId}/${tableId}`;
        const headers = {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
        const payload = {
            status,
            orderId,
            vendorId,
            customerId,
        };

        // console.log('at api: ', payload);
        const response = await axios.put(url, payload, { headers });
        // console.log("updateTableStatus success:", response);
        return response;
    } catch (err) {
        console.error("updateTableStatus error:", err);
    }
};

// Function to send print request to a printer
export const sendToPrinter = async (
    vendorId: number,
    orderId: number,
    printEvent?: string | null,
    printerLocation?: string | null,
) => {
    const token = localStorage.getItem('token'); // Retrieve the stored token
    const params: Record<string, string> = {};

    // Add optional parameters if provided
    if (printEvent) params.printEvent = printEvent;
    if (printerLocation) params.printerLocation = printerLocation;

    try {
        const response = await api.get(`/Pos/PrintReceipt/${vendorId}/${orderId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params, // Append the optional query parameters
        });

        // console.log('sendToPrinter response:', token, response);

        return response.data;
    } catch (error) {
        console.error("Error sending to printer:", error);
        throw error;
    }
};

